import * as logger from 'OK/logger';
import {isDebug} from "OK/banners/bannerNew/utils/BannerCommon";

const ON_REQUEST = 'onRequest';
const ON_ADS_SUCCESS = 'onAdsSuccess';
const ON_NO_ADS = 'onNoAds';
const ON_SCRIPT_ERROR = 'onScriptError';
const ON_LOAD_SUCCESS = 'onLoadSuccess';
const ON_LOAD_ERROR = 'onLoadError';

interface IResponse {
    /**
     * тип транспорта
     */
    transport: string,
    /**
     * id слота
     */
    slot: number,
    /**
     * см. BannerPlace.java
     */
    place: string,
    /**
     * элемент-контейнер баннера
     */
    el: HTMLElement
}

let LISTENERS_BY_EVENTS: {[key: string]: any} = {};

/**
 * Вызывается при запросе баннера
 */
export function onRequest(resp: IResponse) {
    publishEvent(ON_REQUEST, resp);
}

/**
 * Вызывается для каждого непустого баннера в ответе от RB
 */
export function onAdsSuccess(resp: IResponse) {
    publishEvent(ON_ADS_SUCCESS, resp);
}

/**
 * Вызывается для каждого пустого баннера в ответе от RB
 */
export function onNoAds(resp: IResponse) {
    publishEvent(ON_NO_ADS, resp);
}

/**
 * Вызывается при ошибке выполнения inline-скриптов баннера
 */
export function onScriptError(resp: IResponse) {
    publishEvent(ON_SCRIPT_ERROR, resp);
}

export function onLoadSuccess(resp: IResponse) {
    publishEvent(ON_LOAD_SUCCESS, resp);
}

export function onLoadError(resp: IResponse) {
    publishEvent(ON_LOAD_ERROR, resp);
}

/**
 * Добавляет слушателя к определенному событию.
 * Возвращает true, если слушатель был добавлен к событию, false - иначе.
 */
function addNewListenerForEvent(eventName: string, newListener: any): boolean {
    if (!eventName || typeof newListener !== 'function') {
        return false;
    }
    if (!LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        LISTENERS_BY_EVENTS[eventName] = []
    }
    const listeners = LISTENERS_BY_EVENTS[eventName];
    listeners.push(newListener);
    return true;
}


/**
 * Удаляет слушателя для определенного события
 */
function removeListenerForEvent(eventName: string, listenerForRemove: any) {
    if (!eventName || !LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        return;
    }
    var listeners = LISTENERS_BY_EVENTS[eventName];
    var removeIdx = listeners.indexOf(listenerForRemove);
    if (removeIdx >= 0) {
        listeners.splice(removeIdx, 1);
    }
}

/**
 * Осуществляет подписку слушателей на события баннеров для определенного места.
 * @param {string} place [при возникновении событий, относящихся к данному месту, будут вызываться callback-функции];
 * @param {Object.<string, Function>} listenersByEvents, где ключ - название события, значение - callback-функция;
 * @returns {Object} объект подписки, у которого можно вызвать метод remove.
 * При его вызове добавленные слушатели событий перестают "слушать".
 */
export function subscribeToEvents(place: string, listenersByEvents: {[key: string]: any}): object {
    let removeInfo = [];
    // добавляем слушателей на конкретные события и запоминаем их для последующего удаления
    for (let eventName in listenersByEvents) {
        if (!listenersByEvents.hasOwnProperty(eventName)) {
            continue;
        }
        const newListener = createDelegate(place, listenersByEvents[eventName]);
        if (addNewListenerForEvent(eventName, newListener)) {
            removeInfo.push({event: eventName, listener: newListener});
        }
    }
    return {
        remove: () => {
            while (removeInfo.length > 0) {
                const info = removeInfo.pop();
                removeListenerForEvent(info?.event!, info?.listener);
            }
        }
    }
}

function createDelegate(targetPlace: string, targetCallback: (r: Record<string, string>) => void) {
    return function (resp: Record<string, string>) {
        if (resp && resp.place === targetPlace) {
            targetCallback.call(null, resp);
        }
    }
}

/**
 * Сигнализирует слушателей о наступлении конкретного события
 */
function publishEvent(eventName: string, eventData: IResponse) {
    logEvent(eventName, eventData);
    if (!LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        return;
    }
    const listeners = LISTENERS_BY_EVENTS[eventName];
    for (let i = 0, len = listeners.length; i < len; i++) {
        const listener = listeners[i];
        if (typeof listener === 'function') {
            listener(eventData);
        }
    }
}

function logEvent(eventName: string, eventData: IResponse) {
    consoleLog(eventName, eventData);
    const bannerId = eventData.place + '.' + eventData.slot;
    const eventId = eventData.transport + '.' + eventName;
    logger.success('banner.event', bannerId, eventId);
}

function consoleLog(eventName: string, eventData: IResponse) {
    if (isDebug) {
        console.log('[BannerPlaceListeners] calling event \'' + eventName
            + '\', transport: \'' + eventData.transport
            + '\', place: \'' + eventData.place
            + '\', slot: ' + eventData.slot
        );
    }
}